import {
  ADMIN_ORDER_DETAILS,
  Box,
  Button,
  Disclaimer,
  Div,
  FinancesLossOrderEntity,
  formatAmount,
  formatDateTimeShort,
  getFinancesLossOrders,
  getTimeZone,
  H5,
  InfoComponentMobile,
  lightTheme,
  ListSpinner,
  ModalPortal,
  NumberType,
  P,
  reportError,
  TableHeader,
} from '@vgrubs/components';
import { isAxiosError } from 'axios';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

const PAGE_SIZE = 20;

// Desktop table column widths — Customer gets more room, amount columns are tighter.
const COL = {
  order: '9%',
  customer: '10%',
  brand: '15%',
  location: '15%',
  date: '15%',
  subtotal: '9%',
  received: '9%',
  paidOut: '9%',
  margin: '9%',
} as const;

const COLUMNS: { label: string; width: string }[] = [
  { label: 'Order', width: COL.order },
  { label: 'Customer', width: COL.customer },
  { label: 'Brand', width: COL.brand },
  { label: 'Location', width: COL.location },
  { label: 'Date Created', width: COL.date },
  { label: 'Subtotal', width: COL.subtotal },
  { label: 'Received', width: COL.received },
  { label: 'Paid Out', width: COL.paidOut },
  { label: 'Loss', width: COL.margin },
];

const moneyOrDash = (cents: number | null) =>
  cents === null ? '—' : formatAmount(cents, NumberType.USD);

const LossAmount = ({ marginCents }: { marginCents: number }) => (
  <P $m="0" $color={lightTheme.primary} $bold>
    {formatAmount(-marginCents, NumberType.USD)}
  </P>
);

const LossOrderRow = ({ order }: { order: FinancesLossOrderEntity }) => {
  const orderIdLabel = order.orderId.toString();
  const customerLabel = order.customerName ?? '—';
  const openNewTab = (path: string) =>
    window.open(path, '_blank', 'noopener,noreferrer');
  const goToOrder = () =>
    openNewTab(ADMIN_ORDER_DETAILS.replace(':id', order.orderId.toString()));
  const goToBrand = () => openNewTab(`/brand/${order.brandId}`);
  const goToLocation = () => openNewTab(`/locations/${order.locationId}`);

  return (
    <Div>
      {/* Desktop: table row — gap/padding match TableHeader for alignment */}
      <Div
        $desktop
        $flex
        $alignCenter
        $gap="12px"
        $p="12px 16px"
        $borderBottom
        $borderColor={lightTheme.lightGray}
      >
        <Div $width={COL.order}>
          <P $m="0" $pointer $hoverUnderline onClick={goToOrder}>
            {orderIdLabel}
          </P>
        </Div>
        <Div $width={COL.customer}>
          <P $m="0">{customerLabel}</P>
        </Div>
        <Div $width={COL.brand}>
          <P $m="0" $pointer $hoverUnderline onClick={goToBrand}>
            {order.brandName}
          </P>
        </Div>
        <Div $width={COL.location}>
          <P $m="0" $pointer $hoverUnderline onClick={goToLocation}>
            {order.locationName}
          </P>
        </Div>
        <Div $width={COL.date}>
          <P $m="0">{formatDateTimeShort(order.createdAt)}</P>
        </Div>
        <Div $width={COL.subtotal}>
          <P $m="0">{moneyOrDash(order.subTotal)}</P>
        </Div>
        <Div $width={COL.received}>
          <P $m="0">{moneyOrDash(order.paymentReportAmount)}</P>
        </Div>
        <Div $width={COL.paidOut}>
          <P $m="0">{moneyOrDash(order.amount)}</P>
        </Div>
        <Div $width={COL.margin}>
          <LossAmount marginCents={order.margin} />
        </Div>
      </Div>

      {/* Mobile: stacked card */}
      <Box
        $mobile
        $flex
        $column
        $gap="4px"
        $mb="8"
        $p="12px 16px"
        $pSM="12px 16px"
        style={{ boxShadow: 'none' }}
      >
        <Div $flex $alignStart $between $gap="8px">
          <Div $minWidth="0">
            <H5 $m="0">{customerLabel}</H5>
          </Div>
          <LossAmount marginCents={order.margin} />
        </Div>
        <InfoComponentMobile
          title="Order"
          component={
            <Disclaimer $m="0" $pointer $hoverUnderline onClick={goToOrder}>
              {orderIdLabel}
            </Disclaimer>
          }
        />
        <InfoComponentMobile
          title="Brand"
          component={
            <Disclaimer $m="0" $pointer $hoverUnderline onClick={goToBrand}>
              {order.brandName}
            </Disclaimer>
          }
        />
        <InfoComponentMobile
          title="Location"
          component={
            <Disclaimer $m="0" $pointer $hoverUnderline onClick={goToLocation}>
              {order.locationName}
            </Disclaimer>
          }
        />
        <InfoComponentMobile
          title="Date Created"
          component={
            <Disclaimer $m="0">
              {formatDateTimeShort(order.createdAt)}
            </Disclaimer>
          }
        />
        <InfoComponentMobile
          title="Subtotal"
          component={
            <Disclaimer $m="0">{moneyOrDash(order.subTotal)}</Disclaimer>
          }
        />
        <InfoComponentMobile
          title="Received"
          component={
            <Disclaimer $m="0">
              {moneyOrDash(order.paymentReportAmount)}
            </Disclaimer>
          }
        />
        <InfoComponentMobile
          title="Paid Out"
          component={
            <Disclaimer $m="0">{moneyOrDash(order.amount)}</Disclaimer>
          }
        />
      </Box>
    </Div>
  );
};

/**
 * "See Orders" trigger for the VR Order Loss chart — opens a modal that
 * paginates `/v1/finances/loss-orders` (desktop table / mobile cards).
 */
export const SeeLossOrdersButton = ({
  from,
  to,
}: {
  from: string;
  to: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [orders, setOrders] = useState<FinancesLossOrderEntity[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [sentinelRef, sentinelInView] = useInView();

  // Reset when the date range changes — the cached list belongs to the prior range.
  useEffect(() => {
    setOrders([]);
    setPage(1);
    setHasMore(true);
  }, [from, to]);

  const fetchPage = async (nextPage: number) => {
    if (loading) return;
    setLoading(true);
    try {
      const res = await getFinancesLossOrders({
        page: nextPage,
        limit: PAGE_SIZE,
        from: DateTime.fromISO(from).startOf('day').toISODate() || '',
        to: DateTime.fromISO(to).endOf('day').toISODate() || '',
        timeZone: getTimeZone,
      });
      const next = res.data.data ?? [];
      setOrders((prev) => (nextPage === 1 ? next : [...prev, ...next]));
      setHasMore(next.length === PAGE_SIZE);
      setPage(nextPage);
    } catch (error) {
      if (
        isAxiosError(error) &&
        error.response?.data.message !== 'Unauthorized'
      ) {
        reportError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  // First fetch when the modal opens.
  useEffect(() => {
    if (isOpen && orders.length === 0 && hasMore && !loading) {
      fetchPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Continue fetching while the sentinel is in view.
  useEffect(() => {
    if (sentinelInView && isOpen && hasMore && !loading && orders.length > 0) {
      fetchPage(page + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sentinelInView]);

  return (
    <>
      <Div $flexNone $mt="10px">
        <Button $wSM="110px" $w="130px" $small onClick={() => setIsOpen(true)}>
          See Orders
        </Button>
      </Div>
      <ModalPortal
        isOpen={isOpen}
        closeModal={() => setIsOpen(false)}
        backgroundColor={lightTheme.white}
        padding="24px"
        logo={false}
        width="1000px"
        maxHeight="600px"
        modalOverflow="auto"
        titleWithExit="VR Order Loss"
      >
        {/* Desktop table header (shared component, same as Deliveries) */}
        <TableHeader columns={COLUMNS} padding="0 16px" gap="12px" />

        {orders.map((o) => (
          <LossOrderRow key={o.orderId} order={o} />
        ))}

        {orders.length === 0 && !loading && (
          <Div $flex $center $height="100px">
            <P $m="0">No loss orders in this period.</P>
          </Div>
        )}

        <ListSpinner loading={loading} observerRef={sentinelRef} />
      </ModalPortal>
    </>
  );
};
